import { Asset, ChartPeriod, HistoricalPoint } from "../types/finance";

function generateTimeSeries(
  basePrice: number,
  volatility: number,
  trendPct: number,
  pointsCount: number,
  timeframe: ChartPeriod,
  benchmarkBase: number = 100
): HistoricalPoint[] {
  const points: HistoricalPoint[] = [];
  let currentPrice = basePrice * (1 - trendPct / 100);
  let currentBenchmark = benchmarkBase * (1 - trendPct / 150);
  const now = new Date();

  for (let i = 0; i < pointsCount; i++) {
    const progress = i / (pointsCount - 1);
    const noise = (Math.random() - 0.48) * (basePrice * volatility);
    currentPrice += (basePrice * (trendPct / 100)) / pointsCount + noise;
    currentBenchmark += (benchmarkBase * (trendPct / 150)) / pointsCount + (Math.random() - 0.48) * (benchmarkBase * 0.015);
    currentPrice = Math.max(currentPrice, basePrice * 0.4);

    let timeLabel = "";
    let dateLabel = "";

    if (timeframe === "1D") {
      const minutesFromOpen = Math.floor(progress * 420);
      const h = 10 + Math.floor(minutesFromOpen / 60);
      const m = minutesFromOpen % 60;
      timeLabel = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      dateLabel = "Hoje";
    } else if (timeframe === "5D") {
      const dayOffset = 5 - Math.floor(progress * 5);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getDate()}/${d.getMonth() + 1}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}`;
    } else if (timeframe === "1M" || timeframe === "6M" || timeframe === "YTD") {
      const days = timeframe === "1M" ? 30 : timeframe === "6M" ? 180 : 240;
      const dayOffset = days - Math.floor(progress * days);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getDate()}/${d.getMonth() + 1}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    } else {
      const years = timeframe === "1A" ? 1 : timeframe === "5A" ? 5 : 10;
      const dayOffset = years * 365 - Math.floor(progress * years * 365);
      const d = new Date(now.getTime() - dayOffset * 24 * 60 * 60 * 1000);
      timeLabel = `${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`;
      dateLabel = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    }

    const candleVariation = currentPrice * (volatility * 0.3);
    const open = currentPrice - (Math.random() - 0.5) * candleVariation;
    const close = currentPrice;
    const high = Math.max(open, close) + Math.random() * candleVariation;
    const low = Math.min(open, close) - Math.random() * candleVariation;

    points.push({
      time: timeLabel,
      date: dateLabel,
      price: Number(currentPrice.toFixed(2)),
      open: Number(open.toFixed(2)),
      high: Number(high.toFixed(2)),
      low: Number(low.toFixed(2)),
      close: Number(close.toFixed(2)),
      volume: Math.floor(100000 + Math.random() * 5000000),
      benchmarkPrice: Number(currentBenchmark.toFixed(2)),
    });
  }

  if (points.length > 0) {
    points[points.length - 1].price = basePrice;
    points[points.length - 1].close = basePrice;
  }

  return points;
}

function buildAssetHistory(basePrice: number, changePercent: number): Record<ChartPeriod, HistoricalPoint[]> {
  return {
    "1D": generateTimeSeries(basePrice, 0.003, changePercent, 36, "1D", 100),
    "5D": generateTimeSeries(basePrice, 0.008, changePercent * 1.8, 30, "5D", 100),
    "1M": generateTimeSeries(basePrice, 0.015, changePercent * 3.2, 30, "1M", 100),
    "6M": generateTimeSeries(basePrice, 0.025, changePercent * 6.5, 30, "6M", 100),
    YTD: generateTimeSeries(basePrice, 0.028, changePercent * 7.1, 32, "YTD", 100),
    "1A": generateTimeSeries(basePrice, 0.035, changePercent * 12.0, 35, "1A", 100),
    "5A": generateTimeSeries(basePrice, 0.05, changePercent * 35.0, 40, "5A", 100),
    MÁX: generateTimeSeries(basePrice, 0.065, changePercent * 80.0, 45, "MÁX", 100),
  };
}

function getSparkline(basePrice: number, isPositive: boolean): number[] {
  const points = [];
  let val = isPositive ? basePrice * 0.98 : basePrice * 1.02;
  for (let i = 0; i < 7; i++) {
    val += (Math.random() - (isPositive ? 0.4 : 0.6)) * (basePrice * 0.006);
    points.push(Number(val.toFixed(2)));
  }
  points[6] = basePrice;
  return points;
}

export const EXTRA_RAW_ASSETS: Asset[] = [
  // ==================== BIG TECH & WALL STREET (EUA) ====================
  {
    id: "aapl",
    ticker: "AAPL",
    name: "Apple Inc.",
    category: "EUA",
    currency: "USD",
    price: 349.51,
    change: 2.15,
    changePercent: 0.62,
    exchange: "NASDAQ",
    sparkline: getSparkline(349.51, true),
    historical: buildAssetHistory(349.51, 0.62),
    metrics: { open: 347.36, high: 351.20, low: 346.80, high52w: 355.00, low52w: 164.08, prevClose: 347.36, volume: "48,2 Mi", marketCap: "US$ 3,42 Tri", peRatio: "34,2x" },
    profile: {
      description: "A Apple projeta, fabrica e comercializa smartphones (iPhone), computadores pessoais (Mac), tablets (iPad), wearables e serviços digitais integrados como Apple Music, iCloud e Apple Pay.",
      sector: "Tecnologia & Hardware",
      headquarters: "Cupertino, Califórnia, EUA",
      websiteUrl: "https://www.apple.com",
      websiteLabel: "Apple Investor Relations",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["msft", "googl", "nasdaq", "sp-500"],
  },
  {
    id: "nvda",
    ticker: "NVDA",
    name: "NVIDIA Corporation",
    category: "EUA",
    currency: "USD",
    price: 200.24,
    change: 3.40,
    changePercent: 1.73,
    exchange: "NASDAQ",
    sparkline: getSparkline(200.24, true),
    historical: buildAssetHistory(200.24, 1.73),
    metrics: { open: 196.84, high: 202.10, low: 195.90, high52w: 215.00, low52w: 75.60, prevClose: 196.84, volume: "62,5 Mi", marketCap: "US$ 3,18 Tri", peRatio: "48,5x" },
    profile: {
      description: "Líder global em computação gráfica acelerada e infraestrutura de hardware para inteligência artificial generativa, pioneira nas arquiteturas de GPUs para data centers de hiperescala.",
      sector: "Semicondutores & IA",
      headquarters: "Santa Clara, Califórnia, EUA",
      websiteUrl: "https://www.nvidia.com",
      websiteLabel: "NVIDIA Corporate",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["amd", "tsla", "nasdaq", "msft"],
  },
  {
    id: "msft",
    ticker: "MSFT",
    name: "Microsoft Corporation",
    category: "EUA",
    currency: "USD",
    price: 555.80,
    change: 4.20,
    changePercent: 0.76,
    exchange: "NASDAQ",
    sparkline: getSparkline(555.80, true),
    historical: buildAssetHistory(555.80, 0.76),
    metrics: { open: 551.60, high: 558.00, low: 550.40, high52w: 570.00, low52w: 388.00, prevClose: 551.60, volume: "22,4 Mi", marketCap: "US$ 3,12 Tri", peRatio: "35,1x" },
    profile: {
      description: "Gigante global de tecnologia, dona da plataforma de nuvem Azure, do ecossistema Microsoft 365, do Windows, LinkedIn e principal investidora corporativa da OpenAI.",
      sector: "Softwares & Nuvem",
      headquarters: "Redmond, Washington, EUA",
      websiteUrl: "https://www.microsoft.com",
      websiteLabel: "Microsoft Investor",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["aapl", "googl", "amzn", "nasdaq"],
  },
  {
    id: "amzn",
    ticker: "AMZN",
    name: "Amazon.com Inc.",
    category: "EUA",
    currency: "USD",
    price: 266.03,
    change: 2.80,
    changePercent: 1.06,
    exchange: "NASDAQ",
    sparkline: getSparkline(266.03, true),
    historical: buildAssetHistory(266.03, 1.06),
    metrics: { open: 263.23, high: 267.50, low: 262.50, high52w: 275.00, low52w: 168.00, prevClose: 263.23, volume: "35,1 Mi", marketCap: "US$ 2,24 Tri", peRatio: "42,8x" },
    profile: {
      description: "Pioneira em comércio eletrônico mundial, infraestrutura em nuvem através da Amazon Web Services (AWS), inteligência artificial e publicidade digital de alta conversão.",
      sector: "E-Commerce & Cloud",
      headquarters: "Seattle, Washington, EUA",
      websiteUrl: "https://www.amazon.com",
      websiteLabel: "Amazon IR",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["msft", "googl", "wmt", "sp-500"],
  },
  {
    id: "googl",
    ticker: "GOOGL",
    name: "Alphabet Inc. (Google)",
    category: "EUA",
    currency: "USD",
    price: 288.93,
    change: 1.95,
    changePercent: 0.68,
    exchange: "NASDAQ",
    sparkline: getSparkline(288.93, true),
    historical: buildAssetHistory(288.93, 0.68),
    metrics: { open: 286.98, high: 290.40, low: 285.80, high52w: 300.00, low52w: 131.00, prevClose: 286.98, volume: "24,8 Mi", marketCap: "US$ 2,15 Tri", peRatio: "25,3x" },
    profile: {
      description: "Holding controladora do Google, YouTube, Android, Google Cloud e das pesquisas pioneiras em modelos de inteligência artificial multimodal Gemini e DeepMind.",
      sector: "Mídia & Tecnologia",
      headquarters: "Mountain View, Califórnia, EUA",
      websiteUrl: "https://abc.xyz",
      websiteLabel: "Alphabet Investor",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["meta", "aapl", "msft", "nasdaq"],
  },
  {
    id: "tsla",
    ticker: "TSLA",
    name: "Tesla Inc.",
    category: "EUA",
    currency: "USD",
    price: 385.19,
    change: 8.12,
    changePercent: 2.15,
    exchange: "NASDAQ",
    sparkline: getSparkline(385.19, true),
    historical: buildAssetHistory(385.19, 2.15),
    metrics: { open: 377.07, high: 391.00, low: 374.50, high52w: 420.00, low52w: 138.80, prevClose: 377.07, volume: "58,3 Mi", marketCap: "US$ 1,21 Tri", peRatio: "98,2x" },
    profile: {
      description: "Empresa pioneira em mobilidade sustentável, produção em massa de veículos elétricos, sistemas autônomos Full Self-Driving (FSD), armazenamento de energia e robótica humanoide.",
      sector: "Automotivo & Energia",
      headquarters: "Austin, Texas, EUA",
      websiteUrl: "https://ir.tesla.com",
      websiteLabel: "Tesla IR",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["nvda", "sp-500", "nasdaq"],
  },
  {
    id: "meta",
    ticker: "META",
    name: "Meta Platforms Inc.",
    category: "EUA",
    currency: "USD",
    price: 630.00,
    change: 5.50,
    changePercent: 0.88,
    exchange: "NASDAQ",
    sparkline: getSparkline(630.00, true),
    historical: buildAssetHistory(630.00, 0.88),
    metrics: { open: 624.50, high: 635.00, low: 622.00, high52w: 660.00, low52w: 420.00, prevClose: 624.50, volume: "14,2 Mi", marketCap: "US$ 1,59 Tri", peRatio: "27,4x" },
    profile: {
      description: "Proprietária das principais plataformas sociais e de mensagens do mundo (Facebook, Instagram, WhatsApp, Threads) e criadora da família de modelos de código aberto Llama.",
      sector: "Redes & Mídia Digital",
      headquarters: "Menlo Park, Califórnia, EUA",
      websiteUrl: "https://investor.atmeta.com",
      websiteLabel: "Meta Investor Relations",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["googl", "aapl", "nasdaq"],
  },
  {
    id: "nflx",
    ticker: "NFLX",
    name: "Netflix Inc.",
    category: "EUA",
    currency: "USD",
    price: 785.40,
    change: 9.20,
    changePercent: 1.18,
    exchange: "NASDAQ",
    sparkline: getSparkline(785.40, true),
    historical: buildAssetHistory(785.40, 1.18),
    metrics: { open: 776.20, high: 792.00, low: 774.00, high52w: 810.00, low52w: 480.00, prevClose: 776.20, volume: "4,6 Mi", marketCap: "US$ 338 Bi", peRatio: "42,1x" },
    profile: {
      description: "Maior serviço de entretenimento por streaming do mundo, com centenas de milhões de assinantes pagos em mais de 190 países.",
      sector: "Mídia & Entretenimento",
      headquarters: "Los Gatos, Califórnia, EUA",
      websiteUrl: "https://ir.netflix.net",
      websiteLabel: "Netflix IR",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["dis", "amzn", "nasdaq"],
  },
  {
    id: "amd",
    ticker: "AMD",
    name: "Advanced Micro Devices",
    category: "EUA",
    currency: "USD",
    price: 156.20,
    change: 2.30,
    changePercent: 1.49,
    exchange: "NASDAQ",
    sparkline: getSparkline(156.20, true),
    historical: buildAssetHistory(156.20, 1.49),
    metrics: { open: 153.90, high: 158.40, low: 152.80, high52w: 227.00, low52w: 110.00, prevClose: 153.90, volume: "28,7 Mi", marketCap: "US$ 252 Bi", peRatio: "45,0x" },
    profile: {
      description: "Desenvolvedora de semicondutores de alto desempenho, incluindo processadores Ryzen, EPYC e aceleradores Instinct para inteligência artificial.",
      sector: "Semicondutores",
      headquarters: "Santa Clara, Califórnia, EUA",
      websiteUrl: "https://ir.amd.com",
      websiteLabel: "AMD Investor",
      exchange: "NASDAQ",
      currency: "USD",
    },
    relatedAssetIds: ["nvda", "msft", "nasdaq"],
  },
  {
    id: "baba",
    ticker: "BABA",
    name: "Alibaba Group Holding",
    category: "EUA",
    currency: "USD",
    price: 98.40,
    change: 1.10,
    changePercent: 1.13,
    exchange: "NYSE",
    sparkline: getSparkline(98.40, true),
    historical: buildAssetHistory(98.40, 1.13),
    metrics: { open: 97.30, high: 99.80, low: 96.90, high52w: 117.00, low52w: 68.00, prevClose: 97.30, volume: "18,2 Mi", marketCap: "US$ 235 Bi", peRatio: "14,8x" },
    profile: {
      description: "Conglomerado líder em comércio eletrônico na Ásia, computação em nuvem (AliCloud), pagamentos digitais e soluções de logística inteligente.",
      sector: "E-Commerce & Tech",
      headquarters: "Hangzhou, China / ADR NYSE",
      websiteUrl: "https://www.alibabagroup.com",
      websiteLabel: "Alibaba IR",
      exchange: "NYSE",
      currency: "USD",
    },
    relatedAssetIds: ["hang-seng", "amzn"],
  },
  {
    id: "dis",
    ticker: "DIS",
    name: "The Walt Disney Company",
    category: "EUA",
    currency: "USD",
    price: 114.80,
    change: 0.85,
    changePercent: 0.75,
    exchange: "NYSE",
    sparkline: getSparkline(114.80, true),
    historical: buildAssetHistory(114.80, 0.75),
    metrics: { open: 113.95, high: 115.80, low: 113.40, high52w: 123.00, low52w: 83.00, prevClose: 113.95, volume: "9,8 Mi", marketCap: "US$ 208 Bi", peRatio: "22,5x" },
    profile: {
      description: "Empresa diversificada de entretenimento mundial, detentora de parques temáticos, estúdios cinematográficos (Marvel, Lucasfilm, Pixar) e o Disney+.",
      sector: "Mídia & Lazer",
      headquarters: "Burbank, Califórnia, EUA",
      websiteUrl: "https://thewaltdisneycompany.com",
      websiteLabel: "Disney IR",
      exchange: "NYSE",
      currency: "USD",
    },
    relatedAssetIds: ["nflx", "dow-jones"],
  },
  {
    id: "jpm",
    ticker: "JPM",
    name: "JPMorgan Chase & Co.",
    category: "EUA",
    currency: "USD",
    price: 224.50,
    change: 1.60,
    changePercent: 0.72,
    exchange: "NYSE",
    sparkline: getSparkline(224.50, true),
    historical: buildAssetHistory(224.50, 0.72),
    metrics: { open: 222.90, high: 226.10, low: 222.10, high52w: 232.00, low52w: 165.00, prevClose: 222.90, volume: "11,5 Mi", marketCap: "US$ 642 Bi", peRatio: "12,8x" },
    profile: {
      description: "Maior instituição bancária dos Estados Unidos por ativos, atuante em banco de investimentos, gestão de fortunas, tesouraria e varejo.",
      sector: "Serviços Financeiros",
      headquarters: "Nova York, EUA",
      websiteUrl: "https://www.jpmorganchase.com",
      websiteLabel: "JPMorgan Chase IR",
      exchange: "NYSE",
      currency: "USD",
    },
    relatedAssetIds: ["dow-jones", "sp-500"],
  },
  {
    id: "wmt",
    ticker: "WMT",
    name: "Walmart Inc.",
    category: "EUA",
    currency: "USD",
    price: 84.10,
    change: 0.45,
    changePercent: 0.54,
    exchange: "NYSE",
    sparkline: getSparkline(84.10, true),
    historical: buildAssetHistory(84.10, 0.54),
    metrics: { open: 83.65, high: 84.80, low: 83.40, high52w: 88.00, low52w: 52.00, prevClose: 83.65, volume: "16,1 Mi", marketCap: "US$ 678 Bi", peRatio: "32,4x" },
    profile: {
      description: "Maior varejista do planeta em receita, operando redes de hipermercados, lojas de departamento e um ecossistema acelerado de comércio digital.",
      sector: "Varejo & Consumo",
      headquarters: "Bentonville, Arkansas, EUA",
      websiteUrl: "https://corporate.walmart.com",
      websiteLabel: "Walmart Corporate",
      exchange: "NYSE",
      currency: "USD",
    },
    relatedAssetIds: ["amzn", "dow-jones"],
  },

  // ==================== AÇÕES BRASIL (B3) ====================
  {
    id: "bbas3",
    ticker: "BBAS3",
    name: "Banco do Brasil ON",
    category: "América Latina",
    currency: "BRL",
    price: 28.90,
    change: 0.42,
    changePercent: 1.47,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(28.90, true),
    historical: buildAssetHistory(28.90, 1.47),
    metrics: { open: 28.48, high: 29.15, low: 28.40, high52w: 32.50, low52w: 24.10, prevClose: 28.48, volume: "R$ 680 Mi", marketCap: "R$ 82,8 Bi", peRatio: "4,6x", dividendYield: "9,8%" },
    profile: {
      description: "Mais antigo banco em atividade no Brasil, líder inconteste em crédito para o agronegócio e forte pagador de proventos aos acionistas.",
      sector: "Financeiro & Bancário",
      headquarters: "Brasília, DF, Brasil",
      websiteUrl: "https://ri.bb.com.br",
      websiteLabel: "Banco do Brasil RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["itub4", "bbdc4", "ibovespa"],
  },
  {
    id: "b3sa3",
    ticker: "B3SA3",
    name: "B3 S.A. ON",
    category: "América Latina",
    currency: "BRL",
    price: 11.45,
    change: 0.22,
    changePercent: 1.96,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(11.45, true),
    historical: buildAssetHistory(11.45, 1.96),
    metrics: { open: 11.23, high: 11.60, low: 11.15, high52w: 14.80, low52w: 9.80, prevClose: 11.23, volume: "R$ 510 Mi", marketCap: "R$ 64,2 Bi", peRatio: "14,2x" },
    profile: {
      description: "A B3 é a bolsa oficial do Brasil, responsável pela infraestrutura centralizada de negociação, custódia e liquidação de ações, derivativos e renda fixa.",
      sector: "Infraestrutura Financeira",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.b3.com.br",
      websiteLabel: "B3 Relações com Investidores",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "itub4"],
  },
  {
    id: "prio3",
    ticker: "PRIO3",
    name: "PRIO S.A. ON",
    category: "América Latina",
    currency: "BRL",
    price: 45.30,
    change: 1.40,
    changePercent: 3.19,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(45.30, true),
    historical: buildAssetHistory(45.30, 3.19),
    metrics: { open: 43.90, high: 45.90, low: 43.80, high52w: 52.00, low52w: 38.00, prevClose: 43.90, volume: "R$ 380 Mi", marketCap: "R$ 40,1 Bi", peRatio: "8,4x" },
    profile: {
      description: "A PRIO é a maior empresa independente de petróleo e gás do Brasil, com foco na revitalização e operação eficiente de campos maduros no offshore.",
      sector: "Petróleo & Gás",
      headquarters: "Rio de Janeiro, RJ, Brasil",
      websiteUrl: "https://ri.prio3.com.br",
      websiteLabel: "PRIO RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["petr4", "petroleo-brent"],
  },
  {
    id: "embra3",
    ticker: "EMBR3",
    name: "Embraer ON",
    category: "América Latina",
    currency: "BRL",
    price: 57.20,
    change: 2.10,
    changePercent: 3.81,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(57.20, true),
    historical: buildAssetHistory(57.20, 3.81),
    metrics: { open: 55.10, high: 58.00, low: 54.80, high52w: 62.00, low52w: 22.00, prevClose: 55.10, volume: "R$ 420 Mi", marketCap: "R$ 42,3 Bi", peRatio: "26,1x" },
    profile: {
      description: "Líder global na fabricação de jatos comerciais de até 150 assentos, aviação executiva, defesa e soluções de mobilidade aérea urbana (Eve eVTOL).",
      sector: "Aeroespacial & Defesa",
      headquarters: "São José dos Campos, SP, Brasil",
      websiteUrl: "https://ri.embraer.com.br",
      websiteLabel: "Embraer RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "wege3"],
  },
  {
    id: "mglu3",
    ticker: "MGLU3",
    name: "Magazine Luiza ON",
    category: "América Latina",
    currency: "BRL",
    price: 9.85,
    change: -0.25,
    changePercent: -2.48,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(9.85, false),
    historical: buildAssetHistory(9.85, -2.48),
    metrics: { open: 10.10, high: 10.25, low: 9.70, high52w: 16.50, low52w: 7.20, prevClose: 10.10, volume: "R$ 490 Mi", marketCap: "R$ 7,1 Bi" },
    profile: {
      description: "Uma das principais redes varejistas e plataformas de comércio eletrônico multicanal do Brasil, integrando lojas físicas, logística expressa e fintech.",
      sector: "Consumo & Varejo",
      headquarters: "Franca, SP, Brasil",
      websiteUrl: "https://ri.magazineluiza.com.br",
      websiteLabel: "Magalu RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa", "amzn"],
  },
  {
    id: "rent3",
    ticker: "RENT3",
    name: "Localiza Rent a Car ON",
    category: "América Latina",
    currency: "BRL",
    price: 42.10,
    change: 0.65,
    changePercent: 1.57,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(42.10, true),
    historical: buildAssetHistory(42.10, 1.57),
    metrics: { open: 41.45, high: 42.80, low: 41.20, high52w: 58.00, low52w: 36.00, prevClose: 41.45, volume: "R$ 310 Mi", marketCap: "R$ 44,7 Bi", peRatio: "16,4x" },
    profile: {
      description: "Maior empresa de locação de automóveis e gestão de frotas da América Latina, com presença consolidada em centenas de cidades e aeroportos.",
      sector: "Transporte & Aluguel",
      headquarters: "Belo Horizonte, MG, Brasil",
      websiteUrl: "https://ri.localiza.com",
      websiteLabel: "Localiza RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },
  {
    id: "abev3",
    ticker: "ABEV3",
    name: "Ambev S.A. ON",
    category: "América Latina",
    currency: "BRL",
    price: 12.30,
    change: 0.10,
    changePercent: 0.82,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(12.30, true),
    historical: buildAssetHistory(12.30, 0.82),
    metrics: { open: 12.20, high: 12.45, low: 12.15, high52w: 14.20, low52w: 11.00, prevClose: 12.20, volume: "R$ 290 Mi", marketCap: "R$ 193 Bi", peRatio: "13,1x", dividendYield: "6,5%" },
    profile: {
      description: "Maior cervejaria da América Latina e subsidiária da Anheuser-Busch InBev, dona de marcas consolidadas como Brahma, Skol, Antarctica, Budweiser e Stella Artois.",
      sector: "Bebidas & Consumo Não Cíclico",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.ambev.com.br",
      websiteLabel: "Ambev RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },
  {
    id: "suzb3",
    ticker: "SUZB3",
    name: "Suzano ON",
    category: "América Latina",
    currency: "BRL",
    price: 58.40,
    change: 0.80,
    changePercent: 1.39,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(58.40, true),
    historical: buildAssetHistory(58.40, 1.39),
    metrics: { open: 57.60, high: 59.10, low: 57.30, high52w: 64.00, low52w: 48.00, prevClose: 57.60, volume: "R$ 340 Mi", marketCap: "R$ 77,2 Bi", peRatio: "9,8x" },
    profile: {
      description: "Maior produtora global de celulose de eucalipto e uma das maiores fabricantes de papéis da América Latina, altamente exportadora e geradora de divisas.",
      sector: "Papel & Celulose",
      headquarters: "Salvador / São Paulo, Brasil",
      websiteUrl: "https://ri.suzano.com.br",
      websiteLabel: "Suzano RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["vale3", "ibovespa"],
  },
  {
    id: "jbss3",
    ticker: "JBSS3",
    name: "JBS S.A. ON",
    category: "América Latina",
    currency: "BRL",
    price: 36.20,
    change: 0.45,
    changePercent: 1.26,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(36.20, true),
    historical: buildAssetHistory(36.20, 1.26),
    metrics: { open: 35.75, high: 36.70, low: 35.60, high52w: 39.50, low52w: 21.00, prevClose: 35.75, volume: "R$ 260 Mi", marketCap: "R$ 80,3 Bi", peRatio: "11,2x" },
    profile: {
      description: "Uma das maiores indústrias de alimentos do planeta e líder mundial no processamento de proteína animal bovina, suína e de aves com operações globais.",
      sector: "Alimentos & Proteína",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.jbs.com.br",
      websiteLabel: "JBS RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },
  {
    id: "totv3",
    ticker: "TOTS3",
    name: "Totvs ON",
    category: "América Latina",
    currency: "BRL",
    price: 32.15,
    change: 0.75,
    changePercent: 2.39,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(32.15, true),
    historical: buildAssetHistory(32.15, 2.39),
    metrics: { open: 31.40, high: 32.60, low: 31.20, high52w: 36.00, low52w: 26.50, prevClose: 31.40, volume: "R$ 180 Mi", marketCap: "R$ 19,8 Bi", peRatio: "28,4x" },
    profile: {
      description: "Líder absoluta no desenvolvimento de softwares de gestão empresarial (ERP) no Brasil, atendendo pequenas, médias e grandes empresas em todos os setores.",
      sector: "Tecnologia & Softwares",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.totvs.com",
      websiteLabel: "Totvs RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },
  {
    id: "sula11",
    ticker: "EQTL3",
    name: "Equatorial Energia ON",
    category: "América Latina",
    currency: "BRL",
    price: 34.90,
    change: 0.48,
    changePercent: 1.39,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(34.90, true),
    historical: buildAssetHistory(34.90, 1.39),
    metrics: { open: 34.42, high: 35.20, low: 34.30, high52w: 37.80, low52w: 29.50, prevClose: 34.42, volume: "R$ 195 Mi", marketCap: "R$ 38,9 Bi", peRatio: "14,5x" },
    profile: {
      description: "Holding atuante no setor de energia elétrica com concessões de distribuição, transmissão, geração renovável e saneamento em múltiplos estados brasileiros.",
      sector: "Utilidade Pública & Energia",
      headquarters: "São Luís / Brasília, Brasil",
      websiteUrl: "https://ri.equatorialenergia.com.br",
      websiteLabel: "Equatorial RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },
  {
    id: "azulp4",
    ticker: "AZUL4",
    name: "Azul Linhas Aéreas PN",
    category: "América Latina",
    currency: "BRL",
    price: 4.12,
    change: -0.18,
    changePercent: -4.19,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(4.12, false),
    historical: buildAssetHistory(4.12, -4.19),
    metrics: { open: 4.30, high: 4.35, low: 4.05, high52w: 15.20, low52w: 3.80, prevClose: 4.30, volume: "R$ 210 Mi", marketCap: "R$ 1,5 Bi" },
    profile: {
      description: "Uma das maiores companhias aéreas do Brasil em número de voos e cidades atendidas, pioneira na operação de rotas regionais exclusivas.",
      sector: "Aviação & Transporte",
      headquarters: "Barueri, SP, Brasil",
      websiteUrl: "https://ri.voeazul.com.br",
      websiteLabel: "Azul RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["embra3", "ibovespa"],
  },
  {
    id: "cmin3",
    ticker: "CMIN3",
    name: "CSN Mineração ON",
    category: "América Latina",
    currency: "BRL",
    price: 5.48,
    change: -0.12,
    changePercent: -2.14,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(5.48, false),
    historical: buildAssetHistory(5.48, -2.14),
    metrics: { open: 5.60, high: 5.68, low: 5.42, high52w: 7.40, low52w: 4.90, prevClose: 5.60, volume: "R$ 195 Mi", marketCap: "R$ 30,1 Bi", peRatio: "8,9x", dividendYield: "8,4%" },
    profile: {
      description: "Segunda maior exportadora de minério de ferro do Brasil, controlada pela Companhia Siderúrgica Nacional e operando na mina de Casa de Pedra em Congonhas.",
      sector: "Mineração",
      headquarters: "Congonhas, MG, Brasil",
      websiteUrl: "https://ri.csnmineracao.com.br",
      websiteLabel: "CSN Mineração RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["vale3", "csna3", "ibovespa"],
  },
  {
    id: "csna3",
    ticker: "CSNA3",
    name: "Siderúrgica Nacional ON",
    category: "América Latina",
    currency: "BRL",
    price: 10.35,
    change: -0.15,
    changePercent: -1.43,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(10.35, false),
    historical: buildAssetHistory(10.35, -1.43),
    metrics: { open: 10.50, high: 10.65, low: 10.25, high52w: 16.80, low52w: 9.80, prevClose: 10.50, volume: "R$ 280 Mi", marketCap: "R$ 13,8 Bi" },
    profile: {
      description: "Um dos maiores complexos siderúrgicos integrados da América Latina, com atuações sinérgicas em siderurgia, mineração de ferro, cimento e logística portuária.",
      sector: "Siderurgia & Metais",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.csn.com.br",
      websiteLabel: "CSN RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["cmin3", "vale3", "ibovespa"],
  },
  {
    id: "crfb3",
    ticker: "CRFB3",
    name: "Carrefour Brasil ON",
    category: "América Latina",
    currency: "BRL",
    price: 8.72,
    change: -0.08,
    changePercent: -0.91,
    exchange: "B3 (Brasil)",
    sparkline: getSparkline(8.72, false),
    historical: buildAssetHistory(8.72, -0.91),
    metrics: { open: 8.80, high: 8.95, low: 8.65, high52w: 12.80, low52w: 7.90, prevClose: 8.80, volume: "R$ 140 Mi", marketCap: "R$ 18,4 Bi" },
    profile: {
      description: "Maior grupo de varejo alimentar do Brasil, operando as bandeiras Carrefour, Atacadão e Sam's Club em todo o território nacional.",
      sector: "Varejo & Alimentos",
      headquarters: "São Paulo, SP, Brasil",
      websiteUrl: "https://ri.grupocarrefourbrasil.com.br",
      websiteLabel: "Carrefour Brasil RI",
      exchange: "B3",
      currency: "BRL",
    },
    relatedAssetIds: ["ibovespa"],
  },

  // ==================== MOEDAS & CÂMBIO ADICIONAIS ====================
  {
    id: "cad-brl",
    ticker: "CAD / BRL",
    name: "Dólar Canadense / Real",
    category: "Moedas",
    currency: "BRL",
    price: 3.92,
    change: -0.01,
    changePercent: -0.25,
    exchange: "Mercado Interbancário",
    sparkline: getSparkline(3.92, false),
    historical: buildAssetHistory(3.92, -0.25),
    metrics: { open: 3.93, high: 3.95, low: 3.91, high52w: 4.25, low52w: 3.65, prevClose: 3.93, volume: "US$ 640 Mi" },
    profile: {
      description: "Taxa de câmbio representativa entre a moeda canadense e o Real brasileiro, influenciada pelos mercados globais de commodities e energia.",
      sector: "Câmbio & Forex",
      headquarters: "Ottawa, Canadá / Banco Central",
      websiteUrl: "https://www.bankofcanada.ca",
      websiteLabel: "Bank of Canada",
      exchange: "Forex",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "eur-brl"],
  },
  {
    id: "aud-brl",
    ticker: "AUD / BRL",
    name: "Dólar Australiano / Real",
    category: "Moedas",
    currency: "BRL",
    price: 3.56,
    change: 0.01,
    changePercent: 0.28,
    exchange: "Mercado Interbancário",
    sparkline: getSparkline(3.56, true),
    historical: buildAssetHistory(3.56, 0.28),
    metrics: { open: 3.55, high: 3.59, low: 3.54, high52w: 3.85, low52w: 3.25, prevClose: 3.55, volume: "US$ 520 Mi" },
    profile: {
      description: "Cotação do Dólar Australiano frente ao Real, com alta correlação ao ciclo do minério de ferro, carvão e demanda industrial asiática.",
      sector: "Câmbio & Forex",
      headquarters: "Sydney, Austrália",
      websiteUrl: "https://www.rba.gov.au",
      websiteLabel: "Reserve Bank of Australia",
      exchange: "Forex",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "vale3"],
  },
  {
    id: "cny-brl",
    ticker: "CNY / BRL",
    name: "Yuan Chinês / Real",
    category: "Moedas",
    currency: "BRL",
    price: 0.76,
    change: 0.002,
    changePercent: 0.26,
    exchange: "Mercado Interbancário",
    sparkline: getSparkline(0.76, true),
    historical: buildAssetHistory(0.76, 0.26),
    metrics: { open: 0.758, high: 0.764, low: 0.756, high52w: 0.82, low52w: 0.68, prevClose: 0.758, volume: "US$ 1,2 Bi" },
    profile: {
      description: "Moeda oficial da China (Renminbi/Yuan), fundamental no comércio bilateral Brasil-China para exportações de soja, petróleo e minério.",
      sector: "Câmbio & Forex",
      headquarters: "Pequim, China",
      websiteUrl: "http://www.pbc.gov.cn",
      websiteLabel: "People's Bank of China",
      exchange: "Forex",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl", "sse-composite"],
  },
  {
    id: "ars-brl",
    ticker: "ARS / BRL",
    name: "Peso Argentino / Real",
    category: "Moedas",
    currency: "BRL",
    price: 0.0054,
    change: -0.0001,
    changePercent: -1.82,
    exchange: "Mercado Interbancário",
    sparkline: getSparkline(0.0054, false),
    historical: buildAssetHistory(0.0054, -1.82),
    metrics: { open: 0.0055, high: 0.0056, low: 0.0053, high52w: 0.0075, low52w: 0.0048, prevClose: 0.0055, volume: "US$ 180 Mi" },
    profile: {
      description: "Cotação oficial do Peso Argentino frente ao Real brasileiro, refletindo a dinâmica comercial e os acordos no âmbito do Mercosul.",
      sector: "Câmbio & Forex",
      headquarters: "Buenos Aires, Argentina",
      websiteUrl: "https://www.bcra.gob.ar",
      websiteLabel: "Banco Central de la República Argentina",
      exchange: "Forex",
      currency: "BRL",
    },
    relatedAssetIds: ["usd-brl"],
  },

  // ==================== CRIPTOMOEDAS ADICIONAIS ====================
  {
    id: "ada",
    ticker: "ADA",
    name: "Cardano",
    category: "Criptomoedas",
    currency: "USD",
    price: 0.42,
    change: 0.015,
    changePercent: 3.70,
    exchange: "Cripto 24/7",
    sparkline: getSparkline(0.42, true),
    historical: buildAssetHistory(0.42, 3.70),
    metrics: { open: 0.405, high: 0.435, low: 0.400, high52w: 0.81, low52w: 0.28, prevClose: 0.405, volume: "US$ 890 Mi", marketCap: "US$ 15,1 Bi" },
    profile: {
      description: "Plataforma blockchain de terceira geração baseada em pesquisa acadêmica revisada por pares e protocolo de consenso Proof-of-Stake Ouroboros.",
      sector: "Smart Contracts & Web3",
      headquarters: "Zug, Suíça",
      websiteUrl: "https://cardano.org",
      websiteLabel: "Cardano Foundation",
      exchange: "Cripto",
      currency: "USD",
    },
    relatedAssetIds: ["solana", "ether", "bitcoin"],
  },
  {
    id: "avax",
    ticker: "AVAX",
    name: "Avalanche",
    category: "Criptomoedas",
    currency: "USD",
    price: 28.50,
    change: 0.95,
    changePercent: 3.45,
    exchange: "Cripto 24/7",
    sparkline: getSparkline(28.50, true),
    historical: buildAssetHistory(28.50, 3.45),
    metrics: { open: 27.55, high: 29.20, low: 27.20, high52w: 65.00, low52w: 17.50, prevClose: 27.55, volume: "US$ 640 Mi", marketCap: "US$ 11,4 Bi" },
    profile: {
      description: "Rede descentralizada ultrarrápida voltada para aplicativos de finanças descentralizadas (DeFi) e sub-redes corporativas customizáveis.",
      sector: "Smart Contracts & DeFi",
      headquarters: "Nova York, EUA",
      websiteUrl: "https://www.avax.network",
      websiteLabel: "Avalanche Network",
      exchange: "Cripto",
      currency: "USD",
    },
    relatedAssetIds: ["solana", "ether"],
  },
  {
    id: "link",
    ticker: "LINK",
    name: "Chainlink",
    category: "Criptomoedas",
    currency: "USD",
    price: 12.80,
    change: 0.40,
    changePercent: 3.23,
    exchange: "Cripto 24/7",
    sparkline: getSparkline(12.80, true),
    historical: buildAssetHistory(12.80, 3.23),
    metrics: { open: 12.40, high: 13.10, low: 12.25, high52w: 22.80, low52w: 9.50, prevClose: 12.40, volume: "US$ 450 Mi", marketCap: "US$ 7,8 Bi" },
    profile: {
      description: "Padrão da indústria em redes de oráculos descentralizados, conectando blockchains a dados do mundo real e sistemas de liquidação bancária.",
      sector: "Oráculos & Infraestrutura",
      headquarters: "Global Descentralizado",
      websiteUrl: "https://chain.link",
      websiteLabel: "Chainlink Ecosystem",
      exchange: "Cripto",
      currency: "USD",
    },
    relatedAssetIds: ["ether", "bitcoin"],
  },

  // ==================== COMMODITIES & FUTUROS ADICIONAIS ====================
  {
    id: "cafe-futuro",
    ticker: "CAFÉ",
    name: "Café Arábica Futuro",
    category: "Contratos futuros",
    currency: "USD",
    price: 320.00,
    change: 4.50,
    changePercent: 1.43,
    exchange: "ICE Futures US",
    sparkline: getSparkline(320.00, true),
    historical: buildAssetHistory(320.00, 1.43),
    metrics: { open: 315.50, high: 324.00, low: 314.00, high52w: 345.00, low52w: 175.00, prevClose: 315.50, volume: "42 Mil contratos" },
    profile: {
      description: "Contrato futuro de referência internacional para Café Arábica cotado em centavos de dólar por libra-peso, com o Brasil sendo o maior produtor global.",
      sector: "Commodities Agrícolas",
      headquarters: "Nova York, EUA",
      websiteUrl: "https://www.theice.com/products/15/Coffee-C-Futures",
      websiteLabel: "ICE Coffee C",
      exchange: "ICE Futures US",
      currency: "USD",
    },
    relatedAssetIds: ["soja-futuro", "milho-futuro", "ibovespa"],
  },
  {
    id: "milho-futuro",
    ticker: "MILHO",
    name: "Milho Grão Futuro",
    category: "Contratos futuros",
    currency: "USD",
    price: 435.00,
    change: 2.25,
    changePercent: 0.52,
    exchange: "CBOT (Chicago)",
    sparkline: getSparkline(435.00, true),
    historical: buildAssetHistory(435.00, 0.52),
    metrics: { open: 432.75, high: 438.00, low: 431.50, high52w: 495.00, low52w: 390.00, prevClose: 432.75, volume: "185 Mil contratos" },
    profile: {
      description: "Contrato futuro de grãos de milho negociado na bolsa de Chicago (CBOT), fundamental para a cadeia de proteína animal e biocombustíveis.",
      sector: "Commodities Agrícolas",
      headquarters: "Chicago, EUA",
      websiteUrl: "https://www.cmegroup.com/markets/agriculture/grains/corn.html",
      websiteLabel: "CME Corn Futures",
      exchange: "CBOT",
      currency: "USD",
    },
    relatedAssetIds: ["soja-futuro", "cafe-futuro"],
  },
  {
    id: "cobre-futuro",
    ticker: "COBRE",
    name: "Cobre Futuro High Grade",
    category: "Contratos futuros",
    currency: "USD",
    price: 4.35,
    change: 0.04,
    changePercent: 0.93,
    exchange: "COMEX (NYMEX)",
    sparkline: getSparkline(4.35, true),
    historical: buildAssetHistory(4.35, 0.93),
    metrics: { open: 4.31, high: 4.40, low: 4.29, high52w: 5.20, low52w: 3.65, prevClose: 4.31, volume: "85 Mil contratos" },
    profile: {
      description: "O metal vermelho é o principal termômetro da atividade manufatureira e construção civil global, essencial para a transição energética e redes elétricas.",
      sector: "Metais Industriais",
      headquarters: "Nova York, EUA",
      websiteUrl: "https://www.cmegroup.com/markets/metals/base/copper.html",
      websiteLabel: "CME Copper Futures",
      exchange: "COMEX",
      currency: "USD",
    },
    relatedAssetIds: ["ouro-futuro", "prata", "vale3"],
  },
];
